<template>
  <div class="customer-payments-container">
    <!-- Header -->
    <AppHeader :title="`Payments for ${customerName || 'Customer'}`" />
    
    <!-- Search and Filter Bar -->
    <div class="toolbar">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search payments..."
          @input="filterPayments"
        >
        <i class="fas fa-search"></i>
      </div>
      <div class="filter-options">
        <select v-model="statusFilter" @change="filterPayments">
          <option value="all">All</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>
      <div class="filter-options">
        <select v-model="paymentTypeFilter" @change="filterPayments">
          <option value="all">All Types</option>
          <option value="cash">Cash</option>
          <option value="credit">Credit</option>
          <option value="card">Card</option>
        </select>
      </div>
    </div>

    <!-- Summary Cards -->
<div class="summary-cards">
  <div class="summary-card">
    <div class="summary-title">Total Sales</div>
    <div class="summary-value">{{ filteredPayments.length }}</div>
  </div>
  <div class="summary-card">
    <div class="summary-title">Invoice #</div>
    <div class="summary-value">{{ totalInvoices }}</div>
  </div>
  <div class="summary-card">
    <div class="summary-title">Total</div>
    <div class="summary-value">{{ formatCurrency(totalAmount) }}</div>
  </div>
  <div class="summary-card">
    <div class="summary-title">Outstanding</div>
    <div class="summary-value" :class="{'negative-value': outstandingBalance < 0}">
      {{ formatCurrency(outstandingBalance) }}
    </div>
  </div>
</div>

    <!-- Table Structure -->
    <div class="table-responsive">
      <table id="customerPaymentsTable">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Date</th>
            <th>Payment Method</th>
            <th>Total Amount</th>
            <th>Paid Amount</th>
            <th>Status</th>
            <th>Invoice</th>
            <!-- <th>Operations</th> -->
          </tr>
        </thead>
        <tbody>
          <tr v-for="(payment, index) in paginatedPayments" :key="index">
            <td>{{ payment.id }}</td>
            <td>{{ formatDateTime(payment.orderTime) }}</td>
            <td>{{ getPaymentMethod(payment) }}</td>
            <td>{{ formatCurrency(payment.totalPrice) }}</td>
            <td>{{ formatCurrency(payment.totalPay) }}</td>
            <td :class="getStatusClass(payment)">
              {{ getStatusText(payment) }}
            </td>
            <td>
              <span v-if="payment.invoice">#{{ payment.invoice }}</span>
              <span v-else class="text-muted">N/A</span>
            </td>
            <!-- <td class="action-buttons">
              <button 
                class="btn-view"
                @click="handleViewDetails(payment)"
                data-tooltip="View Details"
              >
                <i class="fas fa-eye"></i>
              </button>
              <button 
                class="btn-receipt"
                @click="handlePrintReceipt(payment)"
                data-tooltip="Print Receipt"
                v-if="payment.isPaid"
              >
                <i class="fas fa-receipt"></i>
              </button>
              <button 
                class="btn-refund"
                @click="handleRefund(payment)"
                data-tooltip="Process Refund"
                v-if="payment.isPaid && !payment.isCanceled"
              >
                <i class="fas fa-undo"></i>
              </button>
            </td> -->
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div v-if="filteredPayments.length === 0" class="empty-state">
  <i class="fas fa-file-invoice-dollar"></i>
  <p>{{ emptyStateMessage }}</p>
</div>

    <!-- Pagination -->
    <div v-if="filteredPayments.length > 0" class="pagination">
      <button 
        @click="prevPage" 
        :disabled="currentPage === 1"
        class="page-nav"
      >
        <i class="fas fa-chevron-left"></i>
      </button>
      <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
      <button 
        @click="nextPage" 
        :disabled="currentPage === totalPages"
        class="page-nav"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
       customerId: null,
      payments: [],
      filteredPayments: [],
      searchQuery: '',
      statusFilter: 'all',
      paymentTypeFilter: 'all',
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
      totalInvoices() {
    return new Set(this.filteredPayments.map(p => p.invoice)).size;
  },
   totalAmount() {
    return this.filteredPayments.reduce((sum, payment) => {
      return sum + parseFloat(payment.totalPrice || 0);
    }, 0);
  },
  
  outstandingBalance() {
    return this.filteredPayments.reduce((sum, payment) => {
      return sum + (parseFloat(payment.totalPrice || 0) - (parseFloat(payment.totalPay || 0)));
    }, 0);
  },
    emptyStateMessage() {
    if (this.searchQuery || this.statusFilter !== 'all' || this.paymentTypeFilter !== 'all') {
      return 'No payments match your search criteria';
    }
    return 'No payment records found for this customer';
  },
    totalPages() {
      return Math.ceil(this.filteredPayments.length / this.itemsPerPage);
    },
    paginatedPayments() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPayments.slice(start, end);
    },
    totalPaid() {
      return this.filteredPayments.reduce((sum, payment) => {
        return payment.isPaid ? sum + parseFloat(payment.totalPay) : sum;
      }, 0);
    },
    averagePayment() {
      const paidPayments = this.filteredPayments.filter(p => p.isPaid);
      return paidPayments.length > 0 
        ? this.totalPaid / paidPayments.length 
        : 0;
    }
  },
  mounted() {
    this.customerId = this.$route.params.customerId;
    console.log(this.customerId);
    this.fetchCustomerPayments();
  },
  methods: {
    async fetchCustomerPayments() {
  try {
    this.$root.isLoading = true;
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${this.$BASE_API_URL}/api/orders/paid/${this.customerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to fetch customer payments');
    }

    if (!responseData.status) {
      // API returned status: false with a message
      this.payments = [];
      this.filteredPayments = [];
      this.$toast.info(responseData.message); // Show the API's message
      return;
    }

    this.payments = responseData.data || [];
    this.filteredPayments = [...this.payments];
    
  } catch (error) {
    console.error('Error fetching customer payments:', error);
    this.$toast.error(error.message || 'Failed to load payment data');
    this.payments = [];
    this.filteredPayments = [];
  } finally {
    this.$root.isLoading = false;
  }
},
    filterPayments() {
      let filtered = this.payments.filter(payment => {
        // Search filter
        const matchesSearch = payment.id.toString().includes(this.searchQuery) ||
                            (payment.invoice && payment.invoice.toString().includes(this.searchQuery)) ||
                            payment.payId?.includes(this.searchQuery);
        
        // Status filter
        const matchesStatus = this.statusFilter === 'all' || 
                            (this.statusFilter === 'paid' && payment.isPaid && !payment.isCanceled) ||
                            (this.statusFilter === 'unpaid' && !payment.isPaid) ||
                            (this.statusFilter === 'canceled' && payment.isCanceled);
        
        // Payment type filter
        const matchesPaymentType = this.paymentTypeFilter === 'all' ||
                                 (this.paymentTypeFilter === 'cash' && payment.payWay === 1) ||
                                 (this.paymentTypeFilter === 'credit' && payment.payWay === 2) ||
                                 (this.paymentTypeFilter === 'card' && payment.payWay === 3);
        
        return matchesSearch && matchesStatus && matchesPaymentType;
      });
      
      this.filteredPayments = filtered;
      this.currentPage = 1; // Reset to first page when filtering
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    },
    getPaymentMethod(payment) {
      if (payment.isCanceled) return 'Canceled';
      if (!payment.isPaid) return 'Unpaid';
      
      switch(payment.payWay) {
        case 1: return 'Cash';
        case 2: return 'Credit';
        case 3: return 'Card';
        default: return 'Other';
      }
    },
    getStatusClass(payment) {
      if (payment.isCanceled) return 'status-canceled';
      if (!payment.isPaid) return 'status-unpaid';
      return 'status-paid';
    },
    getStatusText(payment) {
      if (payment.isCanceled) return 'Canceled';
      if (!payment.isPaid) return 'Unpaid';
      return 'Paid';
    },
    handleViewDetails(payment) {
      this.$router.push({ 
        name: "OrderDetails", 
        params: { id: payment.id }
      });
    },
    handlePrintReceipt(payment) {
      // Implement print receipt functionality
      this.$toast.info(`Printing receipt for order #${payment.id}`);
    },
    handleRefund(payment) {
      // Implement refund functionality
      this.$toast.info(`Processing refund for order #${payment.id}`);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    }
  }
};
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  margin: 20px;
  gap: 15px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex-grow: 1;
  max-width: 400px;
}

.search-box input {
  width: 100%;
  padding: 10px 15px 10px 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.filter-options select {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: white;
  font-size: 14px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-inline: 20px;
}


.summary-card {
  padding: 15px;
  border-radius: 8px;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
}

.negative-value {
  color: #dc3545;
}

.summary-title {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 5px;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.status-paid {
  /* color: #28a745; */
  font-weight: 500;
}

.status-unpaid {
  /* color: #ffc107; */
  font-weight: 500;
}

.status-canceled {
  /* color: #dc3545; */
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: transparent;
}

.action-buttons button:hover {
  transform: scale(1.1);
}

.action-buttons button:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #343a40;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 10;
}

.btn-view {
  color: #17a2b8;
}

.btn-view:hover {
  background-color: rgba(23, 162, 184, 0.1);
}

.btn-receipt {
  color: #6c757d;
}

.btn-receipt:hover {
  background-color: rgba(108, 117, 125, 0.1);
}

.btn-refund {
  color: #dc3545;
}

.btn-refund:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 15px;
}

.page-nav {
  width: 36px;
  height: 36px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-nav:hover:not(:disabled) {
  background-color: #f8f9fa;
}

.page-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #6c757d;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
  }
  
  .search-box {
    max-width: 100%;
  }
  
  .summary-cards {
    flex-direction: column;
  }
  
  .summary-card {
    min-width: 100%;
  }
  
  th, td {
    padding: 8px 10px;
    font-size: 14px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .action-buttons button {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  .empty-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
  color: #6c757d;
}

.empty-state p {
  font-size: 16px;
  font-weight: 500;
}
}
</style>